import React from 'react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { ArrowLeft, CheckCircle2, Download, Mail, Paperclip } from 'lucide-react';
import { OOCForecastAdjustment } from '../oocUpdated.type';
import { useOOCCommunicateLogic, getCellClass, periodToLabel } from './oocCommunicate.logic';
import styles from './oocCommunicate.module.scss';

const formatNum = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '-';
  return v.toLocaleString();
};

interface MiniTableProps {
  adjustments: OOCForecastAdjustment[];
  title: string;
  showProbability?: boolean;
}

const MiniEmailTable: React.FC<MiniTableProps> = ({ adjustments, title, showProbability = false }) => {
  if (adjustments.length === 0) return null;
  const monthColumns = adjustments[0]?.forecasts ?? [];

  return (
    <div>
      <div className={styles.sectionTitleRow}>
        <div className={styles.emailSectionTitle}>{title}</div>
        <div className={styles.tableLegend}>
          <span className={styles.increaseArrow}>↑</span> Value adjusted more than 20% above previous
        </div>
      </div>
      <table className={styles.emailTable}>
        <thead>
          <tr>
            <th>NDC10</th>
            <th>NDC11</th>
            <th>Supplier</th>
            <th>Concept</th>
            <th>Pack Out</th>
            <th>Reason</th>
            {monthColumns.map((col) => (
              <th key={col.period}>{periodToLabel(col.period)}</th>
            ))}
            {showProbability && <th>Probability</th>}
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {adjustments.map((adj) => {
            const hasPrev = adj.forecasts.some(
              (c) => c.previousValue !== null && c.previousValue !== undefined
            );
            return (
              <React.Fragment key={adj.id}>
                <tr>
                  <td>{adj.ndc10}</td>
                  <td>{adj.ndc11}</td>
                  <td>{adj.supplier}</td>
                  <td>{adj.concept}</td>
                  <td>{adj.packOut}</td>
                  <td>{adj.reason || '-'}</td>
                  {adj.forecasts.map((cell, idx) => {
                    const isUp =
                      cell.previousValue != null &&
                      cell.previousValue > 0 &&
                      cell.value != null &&
                      cell.value > cell.previousValue * 1.2;
                    return (
                      <td key={idx} className={getCellClass(cell.colorType, styles)}>
                        {formatNum(cell.value)}
                        {isUp && <span className={styles.increaseArrow}>↑</span>}
                      </td>
                    );
                  })}
                  {showProbability && <td>{adj.probability ?? '-'}</td>}
                  <td>{adj.notes || '-'}</td>
                </tr>
                {hasPrev && (
                  <tr className={styles.prevRow}>
                    <td>Previous</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {adj.forecasts.map((cell, idx) => (
                      <td key={idx}>
                        {cell.previousValue != null ? formatNum(cell.previousValue) : ''}
                      </td>
                    ))}
                    {showProbability && <td></td>}
                    <td></td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const OOCCommunicate: React.FC = () => {
  const {
    currentSubmission,
    navigateToDashboard,
    handleExportExcel,
    handleSendEmail,
    handleMarkCommunicated,
    emailData,
  } = useOOCCommunicateLogic();

  if (!currentSubmission || !emailData) {
    return <div className={styles.communicateContainer}>No submission selected.</div>;
  }

  const { id, communicatedDate } = currentSubmission;
  const { masAdj, ncAdj, provMasAdj, provNcAdj, toField, subjectField, attachmentField, greetingText, signatureText } = emailData;

  return (
    <div className={styles.communicateContainer}>
      <div className={styles.sectionHeader}>
        <h2>{id} - Communicated to SC</h2>
      </div>

      <div className={styles.successCallout}>
        <CheckCircle2 size={18} className={styles.calloutIcon} />
        <span>
          Communicated to SC on {communicatedDate ?? 'N/A'}. Email sent to SC, Purchasing, PPS, Business Development, and CSE.
        </span>
      </div>

      <div className={styles.emailPreviewTitle}>Email Preview</div>
      <div className={styles.emailPreviewContainer}>
        <div className={styles.emailHeaderBar}>
          <div className={styles.emailHeaderField}>
            <span className={styles.emailFieldLabel}>To:</span>
            <span className={styles.emailFieldValue}>{toField}</span>
          </div>
          <div className={styles.emailHeaderField}>
            <span className={styles.emailFieldLabel}>Subject:</span>
            <span className={styles.emailFieldValue}>{subjectField}</span>
          </div>
          <div className={styles.emailHeaderField}>
            <span className={styles.emailFieldLabel}>
              <Paperclip size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Attachment:
            </span>
            <span className={styles.emailFieldValue}>{attachmentField}</span>
          </div>
        </div>

        <div className={styles.emailBody}>
          <div className={styles.emailGreeting}>{greetingText}</div>
          <MiniEmailTable adjustments={masAdj} title="MAS DP" />
          <MiniEmailTable adjustments={ncAdj} title="NC DP" />
          <MiniEmailTable adjustments={provMasAdj} title="Provisional MAS" showProbability />
          <MiniEmailTable adjustments={provNcAdj} title="Provisional NC" />
          <div className={styles.emailSignature}>{signatureText}</div>
        </div>
      </div>

      <div className={styles.actionBar}>
        <div className={styles.actionBarLeft}>
          <Button
            label="Back to Dashboard"
            icon={<ArrowLeft size={16} />}
            className={`p-button-text ${styles.backBtn}`}
            onClick={navigateToDashboard}
          />
        </div>
        <div className={styles.actionBarRight}>
          <Button label="Export Excel" icon={<Download size={16} />} className="p-button-outlined" onClick={handleExportExcel} />
          <Button label="Send Email" icon={<Mail size={16} />} className="p-button-outlined" onClick={handleSendEmail} />
          <Button
            label="Close Submission"
            icon={<CheckCircle2 size={16} />}
            className={styles.markCommunicatedBtn}
            onClick={handleMarkCommunicated}
          />
        </div>
      </div>
    </div>
  );
};

export default observer(OOCCommunicate);
